import {
  ESCALATION_TRIGGER_LABELS,
  EscalationTrigger,
  TICKET_PRIORITY_LABELS,
  TICKET_TYPE_LABELS,
  TicketPriority,
  TicketType,
  WEEKDAY_LABELS,
  type BusinessCalendarDto,
  type SlaEscalation,
  type SlaLookups,
  type SlaPolicy,
  type UpsertSlaEscalationRequest,
  type UpsertSlaPolicyRequest,
  type WeeklyHours,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAddSlaHoliday,
  useCreateSlaEscalation,
  useCreateSlaPolicy,
  useDeleteSlaEscalation,
  useRemoveSlaHoliday,
  useSlaCalendars,
  useSlaLookups,
  useSlaPolicies,
  useUpdateSlaCalendar,
  useUpdateSlaEscalation,
  useUpdateSlaPolicy,
} from '@/products/helpdesk/features/sla/use-sla';
import { ApiRequestError } from '@/lib/api';
import { formatSlaMinutes } from '@/lib/format';

const NONE = 'none';
const WEEKDAYS = ['1', '2', '3', '4', '5', '6', '7'] as const;

type PolicyForm = {
  name: string;
  isDefault: boolean;
  isActive: boolean;
  ticketType: string;
  priority: string;
  categoryId: string;
  queueId: string;
  calendarId: string;
  firstResponseMinutes: string;
  resolutionMinutes: string;
};

type EscalationForm = {
  name: string;
  trigger: EscalationTrigger;
  offsetMinutes: string;
  notifyRoleId: string;
  raisePriorityTo: string;
  reassignQueueId: string;
  isActive: boolean;
};

function emptyPolicyForm(calendarId: string): PolicyForm {
  return {
    name: '',
    isDefault: false,
    isActive: true,
    ticketType: NONE,
    priority: NONE,
    categoryId: NONE,
    queueId: NONE,
    calendarId,
    firstResponseMinutes: '480',
    resolutionMinutes: '2400',
  };
}

function fromPolicy(policy: SlaPolicy): PolicyForm {
  return {
    name: policy.name,
    isDefault: policy.isDefault,
    isActive: policy.isActive,
    ticketType: policy.ticketType ?? NONE,
    priority: policy.priority ?? NONE,
    categoryId: policy.categoryId ?? NONE,
    queueId: policy.queueId ?? NONE,
    calendarId: policy.calendarId,
    firstResponseMinutes: String(policy.firstResponseMinutes),
    resolutionMinutes: String(policy.resolutionMinutes),
  };
}

function emptyEscalationForm(): EscalationForm {
  return {
    name: '',
    trigger: EscalationTrigger.FIRST_RESPONSE_WARNING,
    offsetMinutes: '30',
    notifyRoleId: NONE,
    raisePriorityTo: NONE,
    reassignQueueId: NONE,
    isActive: true,
  };
}

function fromEscalation(row: SlaEscalation): EscalationForm {
  return {
    name: row.name,
    trigger: row.trigger,
    offsetMinutes: String(row.offsetMinutes),
    notifyRoleId: row.notifyRoleId ?? NONE,
    raisePriorityTo: row.raisePriorityTo ?? NONE,
    reassignQueueId: row.reassignQueueId ?? NONE,
    isActive: row.isActive,
  };
}

function toPolicyBody(form: PolicyForm): UpsertSlaPolicyRequest {
  return {
    name: form.name.trim(),
    isDefault: form.isDefault,
    isActive: form.isActive,
    ticketType:
      form.ticketType === NONE ? null : (form.ticketType as UpsertSlaPolicyRequest['ticketType']),
    priority: form.priority === NONE ? null : (form.priority as UpsertSlaPolicyRequest['priority']),
    categoryId: form.categoryId === NONE ? null : form.categoryId,
    queueId: form.queueId === NONE ? null : form.queueId,
    calendarId: form.calendarId,
    firstResponseMinutes: Number(form.firstResponseMinutes),
    resolutionMinutes: Number(form.resolutionMinutes),
  };
}

function toEscalationBody(form: EscalationForm): UpsertSlaEscalationRequest {
  return {
    name: form.name.trim(),
    trigger: form.trigger,
    offsetMinutes: Number(form.offsetMinutes),
    notifyRoleId: form.notifyRoleId === NONE ? null : form.notifyRoleId,
    raisePriorityTo:
      form.raisePriorityTo === NONE
        ? null
        : (form.raisePriorityTo as UpsertSlaEscalationRequest['raisePriorityTo']),
    reassignQueueId: form.reassignQueueId === NONE ? null : form.reassignQueueId,
    isActive: form.isActive,
  };
}

function matchLabel(policy: SlaPolicy): string {
  const parts: string[] = [];
  if (policy.isDefault) parts.push('Standard');
  if (policy.ticketType) parts.push(TICKET_TYPE_LABELS[policy.ticketType]);
  if (policy.priority) parts.push(TICKET_PRIORITY_LABELS[policy.priority]);
  if (policy.categoryName) parts.push(policy.categoryName);
  if (policy.queueName) parts.push(policy.queueName);
  return parts.length > 0 ? parts.join(' · ') : 'alle Tickets';
}

export function SlaPage() {
  const lookups = useSlaLookups();
  const calendars = useSlaCalendars();
  const policies = useSlaPolicies();
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);

  const calendarItems = calendars.data?.items ?? [];
  const policyItems = policies.data?.items ?? [];
  const creatingPolicy = selectedPolicyId === 'new';
  const activePolicyId = creatingPolicy ? null : (selectedPolicyId ?? policyItems[0]?.id ?? null);
  const selectedPolicy = activePolicyId
    ? (policyItems.find((item) => item.id === activePolicyId) ?? null)
    : null;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">SLA-Verwaltung</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Policies je Kategorie und Priorität, Geschäftszeiten inklusive Feiertage sowie
          automatische Eskalation. Der Timer pausiert bei Status „Wartet auf Kunde“.
        </p>
      </div>

      {(lookups.isPending || calendars.isPending || policies.isPending) && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          SLA-Daten werden geladen …
        </p>
      )}

      {(lookups.isError || calendars.isError || policies.isError) && (
        <p role="alert" className="text-sm text-destructive">
          Die SLA-Konfiguration konnte nicht geladen werden.
        </p>
      )}

      {calendarItems[0] ? (
        <CalendarCard key={calendarItems[0].updatedAt} calendar={calendarItems[0]} />
      ) : null}

      {lookups.data && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle>Policies</CardTitle>
              <CardDescription>
                Die treffendste aktive Policy gilt zum Zeitpunkt der Ticket-Erstellung.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {policyItems.map((policy) => (
                <Button
                  key={policy.id}
                  type="button"
                  variant={policy.id === activePolicyId ? 'default' : 'outline'}
                  className="h-auto w-full flex-col items-start py-2 text-left"
                  onClick={() => setSelectedPolicyId(policy.id)}
                >
                  <span>{policy.name}</span>
                  <span className="text-xs font-normal opacity-80">{matchLabel(policy)}</span>
                </Button>
              ))}
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => setSelectedPolicyId('new')}
              >
                Neue Policy
              </Button>
            </CardContent>
          </Card>

          {(selectedPolicy != null || creatingPolicy) && (
            <PolicyEditor
              key={selectedPolicy?.id ?? 'new'}
              policy={selectedPolicy}
              lookups={lookups.data}
              defaultCalendarId={calendarItems[0]?.id ?? lookups.data.calendars[0]?.id ?? ''}
              onCreated={(id) => setSelectedPolicyId(id)}
            />
          )}
        </div>
      )}
    </section>
  );
}

function CalendarCard({ calendar }: { calendar: BusinessCalendarDto }) {
  const updateCalendar = useUpdateSlaCalendar();
  const addHoliday = useAddSlaHoliday();
  const removeHoliday = useRemoveSlaHoliday();
  const [name, setName] = useState(calendar.name);
  const [timezone, setTimezone] = useState(calendar.timezone);
  const [hours, setHours] = useState<WeeklyHours>(calendar.weeklyHours);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');

  async function onSaveHours(event: FormEvent) {
    event.preventDefault();
    try {
      await updateCalendar.mutateAsync({
        id: calendar.id,
        body: { name: name.trim(), timezone: timezone.trim(), weeklyHours: hours },
      });
      toast.success('Kalender gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Kalender konnte nicht gespeichert werden.',
      );
    }
  }

  async function onAddHoliday(event: FormEvent) {
    event.preventDefault();
    try {
      await addHoliday.mutateAsync({
        calendarId: calendar.id,
        body: { date: holidayDate, name: holidayName.trim() },
      });
      setHolidayDate('');
      setHolidayName('');
      toast.success('Feiertag hinzugefügt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Feiertag konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Geschäftszeiten</CardTitle>
        <CardDescription>
          {calendar.name} · {calendar.timezone}. Wochenenden und Feiertage zählen nicht zur
          SLA-Zeit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form className="space-y-4" onSubmit={(event) => void onSaveHours(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cal-name">Name</Label>
              <Input
                id="cal-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cal-tz">Zeitzone</Label>
              <Input
                id="cal-tz"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                required
              />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {WEEKDAYS.map((day) => {
              const interval = hours[day][0];
              const open = Boolean(interval);
              return (
                <div key={day} className="flex flex-wrap items-end gap-2 rounded-md border p-3">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={open}
                      onCheckedChange={(checked) => {
                        setHours((current) => ({
                          ...current,
                          [day]: checked === true ? [{ start: '08:00', end: '17:00' }] : [],
                        }));
                      }}
                    />
                    {WEEKDAY_LABELS[day]}
                  </label>
                  <Input
                    type="time"
                    className="w-28"
                    disabled={!open}
                    value={interval?.start ?? '08:00'}
                    onChange={(event) =>
                      setHours((current) => ({
                        ...current,
                        [day]: [
                          {
                            start: event.target.value.slice(0, 5),
                            end: current[day][0]?.end ?? '17:00',
                          },
                        ],
                      }))
                    }
                  />
                  <Input
                    type="time"
                    className="w-28"
                    disabled={!open}
                    value={interval?.end ?? '17:00'}
                    onChange={(event) =>
                      setHours((current) => ({
                        ...current,
                        [day]: [
                          {
                            start: current[day][0]?.start ?? '08:00',
                            end: event.target.value.slice(0, 5),
                          },
                        ],
                      }))
                    }
                  />
                </div>
              );
            })}
          </div>
          <Button type="submit" disabled={updateCalendar.isPending}>
            {updateCalendar.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            Geschäftszeiten speichern
          </Button>
        </form>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Feiertage</h3>
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(event) => void onAddHoliday(event)}
          >
            <div className="space-y-1.5">
              <Label htmlFor="holiday-date">Datum</Label>
              <Input
                id="holiday-date"
                type="date"
                value={holidayDate}
                onChange={(event) => setHolidayDate(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5 sm:flex-1">
              <Label htmlFor="holiday-name">Bezeichnung</Label>
              <Input
                id="holiday-name"
                value={holidayName}
                onChange={(event) => setHolidayName(event.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={addHoliday.isPending}>
              Hinzufügen
            </Button>
          </form>
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {calendar.holidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-muted-foreground">
                      Keine Feiertage hinterlegt.
                    </TableCell>
                  </TableRow>
                ) : (
                  calendar.holidays.map((holiday) => (
                    <TableRow key={holiday.id}>
                      <TableCell>{holiday.date}</TableCell>
                      <TableCell>{holiday.name}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={removeHoliday.isPending}
                          onClick={() =>
                            void removeHoliday
                              .mutateAsync({ calendarId: calendar.id, holidayId: holiday.id })
                              .then(() => toast.success('Feiertag entfernt.'))
                              .catch((error: unknown) =>
                                toast.error(
                                  error instanceof ApiRequestError
                                    ? error.message
                                    : 'Feiertag konnte nicht entfernt werden.',
                                ),
                              )
                          }
                        >
                          Entfernen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PolicyEditor({
  policy,
  lookups,
  defaultCalendarId,
  onCreated,
}: {
  policy: SlaPolicy | null;
  lookups: SlaLookups;
  defaultCalendarId: string;
  onCreated: (id: string) => void;
}) {
  const createPolicy = useCreateSlaPolicy();
  const updatePolicy = useUpdateSlaPolicy();
  const [form, setForm] = useState<PolicyForm>(() =>
    policy ? fromPolicy(policy) : emptyPolicyForm(defaultCalendarId),
  );
  const pending = createPolicy.isPending || updatePolicy.isPending;

  async function onSave(event: FormEvent) {
    event.preventDefault();
    const body = toPolicyBody(form);
    try {
      if (policy) {
        await updatePolicy.mutateAsync({ id: policy.id, body });
        toast.success('Policy gespeichert.');
      } else {
        const created = await createPolicy.mutateAsync(body);
        toast.success('Policy angelegt.');
        onCreated(created.id);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Policy konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{policy ? policy.name : 'Neue Policy'}</CardTitle>
          <CardDescription>
            Reaktions- und Lösungszeit in Geschäftsminuten. Spezifischere Treffer (Kategorie,
            Priorität) gewinnen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={(event) => void onSave(event)}>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="policy-name">Name</Label>
              <Input
                id="policy-name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                required
              />
            </div>
            <OptionalSelect
              id="policy-type"
              label="Tickettyp"
              value={form.ticketType}
              onChange={(ticketType) => setForm((current) => ({ ...current, ticketType }))}
              noneLabel="beliebig"
              options={Object.values(TicketType).map((value) => ({
                value,
                label: TICKET_TYPE_LABELS[value],
              }))}
            />
            <OptionalSelect
              id="policy-priority"
              label="Priorität"
              value={form.priority}
              onChange={(priority) => setForm((current) => ({ ...current, priority }))}
              noneLabel="beliebig"
              options={Object.values(TicketPriority).map((value) => ({
                value,
                label: TICKET_PRIORITY_LABELS[value],
              }))}
            />
            <OptionalSelect
              id="policy-category"
              label="Kategorie"
              value={form.categoryId}
              onChange={(categoryId) => setForm((current) => ({ ...current, categoryId }))}
              noneLabel="beliebig"
              options={lookups.categories.map((item) => ({ value: item.id, label: item.name }))}
            />
            <OptionalSelect
              id="policy-queue"
              label="Warteschlange"
              value={form.queueId}
              onChange={(queueId) => setForm((current) => ({ ...current, queueId }))}
              noneLabel="beliebig"
              options={lookups.queues.map((item) => ({ value: item.id, label: item.name }))}
            />
            <div className="space-y-1.5">
              <Label htmlFor="policy-calendar">Kalender</Label>
              <Select
                value={form.calendarId}
                onValueChange={(calendarId) => setForm((current) => ({ ...current, calendarId }))}
              >
                <SelectTrigger id="policy-calendar" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {lookups.calendars.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-fr">Reaktionszeit (Minuten)</Label>
              <Input
                id="policy-fr"
                type="number"
                min={1}
                value={form.firstResponseMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, firstResponseMinutes: event.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-res">Lösungszeit (Minuten)</Label>
              <Input
                id="policy-res"
                type="number"
                min={1}
                value={form.resolutionMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, resolutionMinutes: event.target.value }))
                }
                required
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, isActive: checked === true }))
                }
              />
              Aktiv
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.isDefault}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, isDefault: checked === true }))
                }
              />
              Fallback (Standard-Policy)
            </label>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={pending}>
                {pending && <Loader2Icon className="animate-spin" aria-hidden />}
                Speichern
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {policy && <EscalationEditor policy={policy} lookups={lookups} />}
    </div>
  );
}

function EscalationEditor({ policy, lookups }: { policy: SlaPolicy; lookups: SlaLookups }) {
  const createEscalation = useCreateSlaEscalation();
  const updateEscalation = useUpdateSlaEscalation();
  const deleteEscalation = useDeleteSlaEscalation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = useMemo(
    () =>
      editingId && editingId !== 'new'
        ? (policy.escalations.find((item) => item.id === editingId) ?? null)
        : null,
    [editingId, policy.escalations],
  );
  const [form, setForm] = useState<EscalationForm>(emptyEscalationForm);

  function startCreate() {
    setEditingId('new');
    setForm(emptyEscalationForm());
  }

  function startEdit(row: SlaEscalation) {
    setEditingId(row.id);
    setForm(fromEscalation(row));
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    const body = toEscalationBody(form);
    try {
      if (editing) {
        await updateEscalation.mutateAsync({ id: editing.id, body });
        toast.success('Eskalationsregel gespeichert.');
      } else {
        await createEscalation.mutateAsync({ policyId: policy.id, body });
        toast.success('Eskalationsregel angelegt.');
      }
      setEditingId(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Regel konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Eskalationsregeln</CardTitle>
        <CardDescription>
          Warnung: Minuten vor der Frist. Verletzung: Minuten nach der Frist (0 = zum
          Fälligkeitszeitpunkt). Teamleiter werden benachrichtigt; optional steigen Priorität oder
          Queue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Regel</TableHead>
                <TableHead>Auslöser</TableHead>
                <TableHead>Offset</TableHead>
                <TableHead>Aktion</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {policy.escalations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    Noch keine Regeln. Der BullMQ-Job sendet bei Verletzung an die hinterlegte
                    Rolle.
                  </TableCell>
                </TableRow>
              ) : (
                policy.escalations.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{ESCALATION_TRIGGER_LABELS[row.trigger]}</TableCell>
                    <TableCell>{formatSlaMinutes(row.offsetMinutes)}</TableCell>
                    <TableCell>
                      {[
                        row.notifyRoleName ? `an ${row.notifyRoleName}` : null,
                        row.raisePriorityTo
                          ? `Priorität ${TICKET_PRIORITY_LABELS[row.raisePriorityTo]}`
                          : null,
                        row.reassignQueueName ? `Queue ${row.reassignQueueName}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'nur benachrichtigen'}
                    </TableCell>
                    <TableCell className="space-x-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(row)}
                      >
                        Bearbeiten
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button type="button" variant="ghost" size="sm">
                            Löschen
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Regel löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              „{row.name}“ wird entfernt. Bereits ausgelöste Ereignisse bleiben in
                              der Historie.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                void deleteEscalation
                                  .mutateAsync(row.id)
                                  .then(() => toast.success('Regel gelöscht.'))
                                  .catch((error: unknown) =>
                                    toast.error(
                                      error instanceof ApiRequestError
                                        ? error.message
                                        : 'Regel konnte nicht gelöscht werden.',
                                    ),
                                  )
                              }
                            >
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {editingId === null ? (
          <Button type="button" variant="outline" onClick={startCreate}>
            Neue Eskalationsregel
          </Button>
        ) : (
          <form
            className="grid gap-4 rounded-md border p-4 sm:grid-cols-2"
            onSubmit={(event) => void onSave(event)}
          >
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="esc-name">Name</Label>
              <Input
                id="esc-name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="esc-trigger">Auslöser</Label>
              <Select
                value={form.trigger}
                onValueChange={(value) =>
                  setForm((current) => ({ ...current, trigger: value as EscalationTrigger }))
                }
              >
                <SelectTrigger id="esc-trigger" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(EscalationTrigger).map((value) => (
                    <SelectItem key={value} value={value}>
                      {ESCALATION_TRIGGER_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="esc-offset">Offset (Minuten)</Label>
              <Input
                id="esc-offset"
                type="number"
                min={0}
                value={form.offsetMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, offsetMinutes: event.target.value }))
                }
                required
              />
            </div>
            <OptionalSelect
              id="esc-role"
              label="Benachrichtigen (Rolle)"
              value={form.notifyRoleId}
              onChange={(notifyRoleId) => setForm((current) => ({ ...current, notifyRoleId }))}
              noneLabel="IT-Leitung (Fallback)"
              options={lookups.roles.map((item) => ({ value: item.id, label: item.name }))}
            />
            <OptionalSelect
              id="esc-priority"
              label="Priorität anheben auf"
              value={form.raisePriorityTo}
              onChange={(raisePriorityTo) =>
                setForm((current) => ({ ...current, raisePriorityTo }))
              }
              noneLabel="unverändert"
              options={Object.values(TicketPriority).map((value) => ({
                value,
                label: TICKET_PRIORITY_LABELS[value],
              }))}
            />
            <OptionalSelect
              id="esc-queue"
              label="Neu zuweisen an Queue"
              value={form.reassignQueueId}
              onChange={(reassignQueueId) =>
                setForm((current) => ({ ...current, reassignQueueId }))
              }
              noneLabel="unverändert"
              options={lookups.queues.map((item) => ({ value: item.id, label: item.name }))}
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, isActive: checked === true }))
                }
              />
              Aktiv
            </label>
            <div className="flex gap-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={createEscalation.isPending || updateEscalation.isPending}
              >
                Speichern
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                Abbrechen
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function OptionalSelect({
  id,
  label,
  value,
  onChange,
  noneLabel,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  noneLabel: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{noneLabel}</SelectItem>
          {options.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
